import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import FilmCard from '@/components/feature/FilmCard';
import { getFilmById, getNewsByFilm, getRelatedFilms } from '@/lib/catalog';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id } = useParams();
  const film = getFilmById(id);

  usePageMeta({
    title: film
      ? `${film.title} - 神宫寺奈绪影视详情 | 神宫寺奈绪影视内容站`
      : '作品不存在 - 神宫寺奈绪影视内容站',
    description: film
      ? `${film.title}（${film.year} ${film.type}）：${film.summary.slice(0, 60)}……查看作品简介、演职员资料与相关资讯。`
      : '该作品不存在或已下架，请返回作品列表浏览其他作品。',
    keywords: film ? `神宫寺奈绪,${film.title},${film.type},${film.genres.join(',')}` : '神宫寺奈绪',
  });

  if (!film) {
    return (
      <div className="px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-lg mx-auto text-center bg-background-100 border border-background-200 rounded-lg p-10">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-3xl">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">作品不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-500">
            您访问的作品可能已下线，或链接有误。可返回作品列表继续浏览其他内容。
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/category"
              className="px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors"
            >
              <i className="ri-movie-2-line"></i>
              返回作品列表
            </Link>
            <Link
              to="/"
              className="px-6 h-11 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm flex items-center gap-2 whitespace-nowrap transition-colors"
            >
              回到首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const relatedNews = getNewsByFilm(film.id);
  const relatedFilms = getRelatedFilms(film, 4);

  return (
    <div className="w-full">
      <section className="px-4 md:px-6 pt-8 md:pt-12 pb-6 bg-background-100 border-b border-background-200">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '影视作品', to: '/category' },
            { label: film.title },
          ]}
        />
      </section>

      <section className="px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* 左栏：封面 + 基础信息 */}
          <aside className="w-full lg:w-[340px] shrink-0">
            <div className="rounded-lg overflow-hidden aspect-[3/4] bg-background-200">
              <img
                src={film.cover}
                alt={`${film.title} 海报`}
                title={`${film.title} 神宫寺奈绪影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 bg-background-100 border border-background-200 rounded-lg p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-4">基础资料</h2>
              <dl className="space-y-3 text-sm">
                {[
                  { label: '类型', value: film.type },
                  { label: '年份', value: String(film.year) },
                  { label: '导演', value: film.director },
                  { label: '主演', value: film.cast },
                  { label: '时长', value: film.duration },
                  { label: '播出平台', value: film.platform },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="text-foreground-500 w-16 shrink-0">{row.label}</dt>
                    <dd className="text-foreground-800 flex-1">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between">
                <span className="text-sm text-foreground-500">观众评分</span>
                <span className="flex items-center gap-1 text-accent-600 font-bold text-lg">
                  <i className="ri-star-fill text-sm"></i>
                  {film.rating.toFixed(1)}
                </span>
              </div>
            </div>
          </aside>

          {/* 右栏：标题、简介、剧照、相关资讯 */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs">{film.type}</span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">{film.year}</span>
              {film.genres.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs">
                  {g}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {film.title}
            </h1>
            <p className="mt-3 text-xs md:text-sm text-foreground-500">
              更新时间：{film.updatedAt} · 已收录关联资讯 {relatedNews.length} 条
            </p>

            <div className="mt-7">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-book-open-line text-accent-600"></i>
                作品简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{film.summary}</p>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-image-line text-accent-600"></i>
                剧照
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {film.gallery.map((img, i) => (
                  <div key={i} className="rounded-lg overflow-hidden aspect-video bg-background-200">
                    <img
                      src={img}
                      alt={`${film.title} 剧照 ${i + 1}`}
                      title={`${film.title} 神宫寺奈绪剧照`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 相关资讯 */}
            <div className="mt-10">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-newspaper-line text-accent-600"></i>
                相关资讯
              </h2>
              {relatedNews.length > 0 ? (
                <div className="space-y-3">
                  {relatedNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex items-start justify-between gap-4 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0">
                        <h3 className="text-sm md:text-base text-foreground-900 font-medium group-hover:text-accent-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 text-xs md:text-sm text-foreground-500 line-clamp-1">
                          {item.summary}
                        </p>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap pt-0.5">{item.date}</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500">暂无与该作品关联的资讯。</p>
              )}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回分类
              </Link>
              <Link
                to="/"
                className="px-6 h-11 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm flex items-center gap-2 whitespace-nowrap transition-colors"
              >
                回到首页
              </Link>
            </div>
          </div>
        </div>
      </section>

      {relatedFilms.length > 0 && (
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">你可能也喜欢</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {relatedFilms.map((f) => (
              <FilmCard key={f.id} film={f} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}